import { Link, useParams, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Navbar } from "@/components/Navbar";
import { SiteFooter } from "@/components/SiteFooter";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { ArrowRight, BookOpen, Lock, Phone } from "lucide-react";
import { useSiteSettings } from "@/lib/settings";
import { TEXT_GUIDES, findTextGuide } from "@/data/textGuides";
import { findService } from "@/data/services";
import { PHONE_DISPLAY, PHONE_HREF } from "@/lib/contact";

const FREE_PAGES = 3;

export default function TextGuidePage() {
  const { slug } = useParams();
  const guide = findTextGuide(slug);
  const settings = useSiteSettings();

  if (!guide) return <Navigate to="/guides" replace />;

  const service = findService(guide.relatedService);
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: `${guide.title} VCE analysis — themes, structure and essay approaches`,
        description: guide.metaDescription,
        about: { "@type": "Book", name: guide.title, author: { "@type": "Person", name: guide.author } },
        author: {
          "@type": "Person",
          name: "Yahav Ben Yosef",
          jobTitle: "Founder & English tutor",
          description: "Scored a raw 50 in VCE English and a 99+ ATAR.",
          url: "https://tutorsmelbourne.com/#about",
          worksFor: { "@type": "EducationalOrganization", name: "Tutors Melbourne" },
        },
        publisher: {
          "@type": "EducationalOrganization",
          name: "Tutors Melbourne",
          logo: { "@type": "ImageObject", url: "https://tutorsmelbourne.com/logo.png" },
        },
        educationalLevel: guide.level,
        inLanguage: "en-AU",
      },
      {
        "@type": "FAQPage",
        mainEntity: guide.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0f1117]" data-testid={`text-guide-${guide.slug}`}>
      <Seo title={guide.metaTitle} rawTitle description={guide.metaDescription} path={`/guides/${guide.slug}`}
        image={`/guides/${guide.slug}.jpg`} jsonLd={jsonLd} />
      <Navbar />

      <section className="pt-28 pb-10 px-6 bg-gradient-to-b from-brand-purple-soft/40 to-white dark:from-brand-purple/10 dark:to-[#0f1117]">
        <div className="max-w-5xl mx-auto grid md:grid-cols-[1fr_260px] gap-10 items-start">
          <Reveal>
            <Badge className="bg-brand-gold text-white mb-4 font-body font-semibold px-4 py-1.5 rounded-full">
              {guide.form} · {guide.level}
            </Badge>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white leading-tight mb-3">
              {guide.title} — VCE analysis
            </h1>
            <p className="font-body text-sm text-slate-500 dark:text-slate-400 mb-5">
              by {guide.author} · guide written by Yahav Ben Yosef (raw 50 English, 99+ ATAR)
            </p>
            <p className="text-lg text-slate-600 dark:text-slate-300 font-body leading-relaxed">{guide.hook}</p>
          </Reveal>
          <Reveal delay={100}>
            <img src={`/guides/${guide.slug}.jpg`} alt={`${guide.title} study guide cover`}
              className="w-full rounded-xl shadow-lg ring-1 ring-black/5" />
          </Reveal>
        </div>
      </section>

      <section className="py-14 px-6">
        <div className="max-w-3xl mx-auto space-y-12">
          <Reveal>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-6">
              The themes that actually earn marks
            </h2>
            <div className="space-y-6">
              {guide.themes.map((t) => (
                <div key={t.name} className="border-l-4 border-brand-purple/30 pl-5">
                  <h3 className="font-heading text-lg font-semibold text-slate-900 dark:text-white mb-1.5">{t.name}</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-300 font-body leading-relaxed">{t.body}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal className="rounded-2xl bg-brand-purple-soft/50 dark:bg-brand-purple/10 p-7 border border-brand-purple/15">
            <h2 className="font-heading text-xl font-bold text-slate-900 dark:text-white mb-3">
              How to write about {guide.title}
            </h2>
            <p className="text-sm text-slate-700 dark:text-slate-300 font-body leading-relaxed">{guide.essayTip}</p>
          </Reveal>

          {/* Free excerpt of the real study guide */}
          <Reveal>
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="h-5 w-5 text-brand-purple" />
              <h2 className="font-heading text-2xl font-bold text-slate-900 dark:text-white">
                Read the first {FREE_PAGES} pages, free
              </h2>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-300 font-body mb-6">
              This is the actual {guide.pages}-page guide our students study from — no email required.
              The full guide comes with a class spot.
            </p>
            <div className="space-y-5" data-testid="guide-excerpt">
              {Array.from({ length: FREE_PAGES }, (_, p) => {
                const n = String(p + 1).padStart(2, "0");
                return (
                  <figure key={n} className="relative">
                    <img src={`/guides/pages/${guide.slug}-${n}.jpg`}
                      alt={`${guide.title} VCE study guide — page ${p + 1}`}
                      loading={p === 0 ? "eager" : "lazy"}
                      className="w-full rounded-lg shadow-md ring-1 ring-black/5 bg-white"
                      data-testid={`excerpt-page-${p + 1}`} />
                    <figcaption className="absolute bottom-2 right-3 rounded-full bg-brand-purple/85 text-white text-[11px] font-body px-2.5 py-0.5">
                      {p + 1} / {guide.pages}
                    </figcaption>
                  </figure>
                );
              })}
            </div>
            <div className="mt-6 rounded-2xl border-2 border-dashed border-brand-purple/25 p-7 text-center">
              <Lock className="h-6 w-6 text-brand-purple mx-auto mb-3" />
              <h3 className="font-heading text-lg font-bold text-slate-900 dark:text-white mb-2">
                {guide.pages - FREE_PAGES} more pages for enrolled students
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-300 font-body mb-5 max-w-md mx-auto">
                Chapter-by-chapter analysis, quote banks, essay plans and sample paragraphs — included with every
                class spot, along with weekly marked essays.
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Link to="/enrol">
                  <Button className="rounded-full bg-brand-purple hover:bg-brand-purple-hover text-white font-body font-semibold px-7" data-testid="guide-enrol-cta">
                    Enrol For {settings.term_label} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <a href={PHONE_HREF}>
                  <Button variant="outline" className="rounded-full border-brand-purple text-brand-purple hover:bg-brand-purple-soft font-body font-semibold px-7">
                    <Phone className="mr-2 h-4 w-4" /> {PHONE_DISPLAY}
                  </Button>
                </a>
              </div>
            </div>
          </Reveal>

          <Reveal>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-6">
              {guide.title} — common questions
            </h2>
            <div className="space-y-5" data-testid="guide-faqs">
              {guide.faqs.map((f) => (
                <div key={f.q} className="border-l-4 border-brand-gold/40 pl-5">
                  <h3 className="font-heading text-lg font-semibold text-slate-900 dark:text-white mb-1.5">{f.q}</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-300 font-body leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </Reveal>

          {service && (
            <Reveal>
              <p className="font-body text-base text-slate-600 dark:text-slate-300 leading-relaxed">
                We teach {guide.title} in our{" "}
                <Link to={`/${service.slug}`} className="text-brand-purple dark:text-brand-purple-light font-semibold underline decoration-brand-gold decoration-2 underline-offset-2"
                  data-testid="guide-service-link">
                  {service.title.toLowerCase()}
                </Link>{" "}
                classes — small groups, weekly marked essays, and the full guide included. You can also{" "}
                <Link to="/blog" className="text-brand-purple dark:text-brand-purple-light font-semibold underline decoration-brand-gold decoration-2 underline-offset-2">
                  read our essay technique articles
                </Link>{" "}
                or see the{" "}
                <Link to="/pricing" className="text-brand-purple dark:text-brand-purple-light font-semibold underline decoration-brand-gold decoration-2 underline-offset-2">
                  class pricing
                </Link>.
              </p>
            </Reveal>
          )}

          <Reveal>
            <h2 className="font-heading text-xl font-bold text-slate-900 dark:text-white mb-4">Other texts we teach</h2>
            <div className="flex flex-wrap gap-2">
              {TEXT_GUIDES.filter((g) => g.slug !== guide.slug).map((g) => (
                <Link key={g.slug} to={`/guides/${g.slug}`}
                  className="rounded-full border border-slate-200 dark:border-slate-700 px-4 py-2 text-sm font-body text-brand-purple dark:text-brand-purple-light hover:border-brand-purple transition-colors"
                  data-testid={`guide-link-${g.slug}`}>
                  {g.title}
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
